// What a feedback report's screenshot shows (2026-10-27): the visible Majlis page as the person sees it, each panel at
// its own scroll position, rendered in this browser (no service, no upload) before the dialog appears. Never shown:
// what's typed in a field (a password always), anything marked `data-feedback-private`, and Feedback's own controls; each
// is painted over with an opaque block where it was, so the layout stays readable. The person reviews it, hides more if
// they want, and only then is it sent.
import { domToCanvas } from "modern-screenshot";

export interface Area { x: number; y: number; w: number; h: number }

const MAX_PIXELS = 4_000_000;
const MAX_BYTES = 2 * 1024 * 1024;

function hiddenFields(): Area[] {
  const areas: Area[] = [];
  const fields = document.querySelectorAll<HTMLElement>(
    "[data-feedback-private], input:not([type=checkbox]):not([type=radio]):not([type=range]):not([type=button]):not([type=submit]), textarea");
  for (const el of Array.from(fields)) {
    if (el.closest("[data-feedback-ui]")) continue;
    const typed = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
      ? el.value !== "" || (el instanceof HTMLInputElement && el.type === "password") : true;
    if (!typed) continue;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1 || r.bottom < 0 || r.right < 0 || r.top > innerHeight || r.left > innerWidth) continue;
    areas.push({ x: Math.max(0, r.left), y: Math.max(0, r.top), w: Math.min(r.width, innerWidth - r.left), h: Math.min(r.height, innerHeight - r.top) });
  }
  return areas;
}

/** Paints `areas` opaque on `canvas` (scaled from CSS pixels), with a faint diagonal so a hidden area reads as hidden. */
export function paint(canvas: HTMLCanvasElement, areas: Area[], scale = 1): void {
  const g = canvas.getContext("2d");
  if (!g) return;
  for (const a of areas) {
    const [x, y, w, h] = [a.x * scale, a.y * scale, a.w * scale, a.h * scale];
    g.fillStyle = "#6b7280";
    g.fillRect(x, y, w, h);
    g.strokeStyle = "rgba(255,255,255,0.18)";
    g.lineWidth = 1;
    g.save();
    g.beginPath();
    g.rect(x, y, w, h);
    g.clip();
    for (let d = -h; d < w; d += 10) { g.beginPath(); g.moveTo(x + d, y + h); g.lineTo(x + d + h, y); g.stroke(); }
    g.restore();
  }
}

/** The visible page, at its own scroll positions, with typed fields and private areas painted over. */
export async function captureView(): Promise<HTMLCanvasElement> {
  const hide = hiddenFields();
  const canvas = await domToCanvas(document.documentElement, {
    width: innerWidth, height: innerHeight, scale: 1,
    backgroundColor: getComputedStyle(document.body).backgroundColor || "#ffffff",
    style: { transform: `translate(${-scrollX}px, ${-scrollY}px)` },
    filter: (node) => !(node instanceof Element && node.closest("[data-feedback-ui]")),
    features: { restoreScrollPosition: true },
  });
  paint(canvas, hide);
  return canvas;
}

/** The final image: `base` with the areas the person hid, as a PNG within 4 megapixels and 2 MiB (scaled down if
 * needed: the person sees the result before sending). */
export async function exportPng(base: HTMLCanvasElement, hidden: Area[]): Promise<{ data: string; width: number; height: number }> {
  let scale = Math.min(1, Math.sqrt(MAX_PIXELS / (base.width * base.height)));
  for (let tries = 0; tries < 6; tries++) {
    const out = document.createElement("canvas");
    out.width = Math.max(1, Math.round(base.width * scale));
    out.height = Math.max(1, Math.round(base.height * scale));
    const g = out.getContext("2d");
    if (!g) throw new Error("no canvas");
    g.drawImage(base, 0, 0, out.width, out.height);
    paint(out, hidden, scale);
    const url = out.toDataURL("image/png");
    const bytes = Math.floor((url.length - "data:image/png;base64,".length) * 3 / 4);
    if (bytes <= MAX_BYTES) return { data: url.slice("data:image/png;base64,".length), width: out.width, height: out.height };
    scale *= 0.75;
  }
  throw new Error("the screenshot stays larger than 2 MiB: hide part of it, or send without it");
}
